/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import type {NextRouter, RouterEvent} from 'next/router';
import * as logger from '@ion/core/src/logger/logger';

/**
 * Defines the structure for navigation parameters used by the RouteManager
 * navigate method, supporting both simple pathname navigation and complex
 * routing with query parameters and history replacement options.
 *
 * @param pathname The target route path for navigation.
 * @param query Optional query parameters to append to the route.
 * @param replace Optional flag to replace current history entry instead of adding new one.
 */
export interface NavigationParams {
  pathname: string;
  query?: string;
  replace?: boolean;
};

/**
 * Defines the navigation pre-processing parameters for Next.js router
 * operations.
 */
type NavigationPreparationParams = {
  method: 'push' | 'replace',
  isValid: boolean,
  params?: string,
  target: string,
}
/**
 * Defines the event listener callback function signature for `RouteManager`,
 * event listeners.
 *
 * @param event The router event being listened for.
 * @param callback The callback function to execute when the event occurs.
 */
export type RouteEventListener = (
  event: RouterEvent, callback: (...args: any[]) => void) => void;

/**
 * Defines the public interface of the `RouteManager`, providing type safety
 * and clear contracts for all public methods available for programmatic
 * navigation and router management.
 */
export interface IRouteManager {
  initialize(instance: NextRouter): IRouteManager;
  addEventListener: RouteEventListener;
  removeEventListener: RouteEventListener;
  navigate(params: NavigationParams): void;
  getAsPath(): string;
  getPathName(): string;
  back(): void;
}

/**
 * Provides a singleton wrapper for managing Next.js router functionality
 * outside of React components, enabling programmatic navigation from Redux
 * actions, utility functions, and other non-React contexts.
 *
 * The RouteManager encapsulates the Next.js router instance and provides
 * controlled access through methods for navigation, path retrieval, and
 * event management. This utility ensures consistent routing behavior across
 * the iOn Platform applications.
 *
 * ```typescript
 * import {useRouter} from 'next/router';
 * import {useEffect} from 'react';
 * import routeManager from '@ion/applications/src/routes/RouteManager';
 * ...
 *
 *   // initialize in _app.tsx or root component ...
 *   const router = useRouter();
 *   useEffect(() => {
 *     routeManager.initialize(router);
 *   }, [router]);
 *
 *   // use anywhere in the application ...
 *   routeManager.navigate({
 *     pathname: '/dashboard',
 *     query: {tab: 'analytics'}
 *   });
 * ```
 *
 * @see {@link NavigationParams}
 */
class RouterManager implements IRouteManager {
  /**
   * @private
   *
   * Provides a reference to the underlying Next.js router instance which is
   * assigned during application startup and used for navigation and event
   * management.
   */
  private _router: NextRouter | null = null;

  /**
   * Initializes the RouteManager with a Next.js router instance, enabling
   * programmatic navigation throughout the application. This method must be
   * called once during application startup before any navigation operations.
   *
   * ```typescript
   * import {useRouter} from 'next/router';
   * import routeManager from '@ion/applications/src/routes/RouteManager';
   * ...
   *
   *   const router = useRouter();
   *   routeManager.initialize(router);
   * ```
   *
   * @param instance The Next.js router instance obtained from useRouter hook.
   */
  public initialize = (instance): IRouteManager => {
    if (!this._router && instance) {
      this._router = instance;
      this.log('Application Route Manager Initialized', 'info');
    } else {
      this.log('Application Route Manager Already Initialized', 'warn');
    }
    return this;
  };

  /**
   * Retrieves the current browser path including dynamic segments, query
   * parameters, and hash fragments. This represents the actual URL displayed
   * in the browser address bar.
   *
   * ```typescript
   * import routeManager from '@ion/applications/src/routes/RouteManager';
   * ...
   *
   *   // returns '/dashboard/analytics?tab=reports#section1' ...
   *   const currentPath = routeManager.getAsPath();
   * ```
   *
   * @returns The complete browser path or empty string when not initialized.
   */
  public getAsPath = (): string => this._router?.asPath || '';

  /**
   * Retrieves the logical route pathname from Next.js routing system,
   * excluding query parameters and hash fragments. This is useful for route
   * matching, active page identification, and navigation logic.
   *
   * ```typescript
   * import routeManager from '@ion/applications/src/routes/RouteManager';
   * ...
   *
   *   // returns '/dashboard/analytics' (without query params) ...
   *   const routePath = routeManager.getPathName();
   *   if (routePath === '/dashboard') {
   *     // handle dashboard route logic ...
   *   }
   * ```
   *
   * @returns The logical route pathname or empty string when not initialized.
   */
  public getPathName = (): string => this._router?.pathname || '';

  /**
   * Navigates to the previous page in the browser history stack using
   * Next.js routing context. This provides consistent back navigation
   * behavior across the iOn Platform applications.
   *
   * ```typescript
   * import routeManager from '@ion/applications/src/routes/RouteManager';
   * ...
   *
   *   // navigate back to previous page ...
   *   const handleCancel = () => {
   *     routeManager.back();
   *   };
   * ```
   */
  public back = (): void => this._router?.back();

  /**
   * Registers an event listener for Next.js router events, enabling
   * applications to respond to navigation state changes such as route
   * transitions, loading states, and navigation completion.
   *
   * ```typescript
   * import routeManager from '@ion/applications/src/routes/RouteManager';
   * ...
   *
   *   const handleRouteStart = (url) => {
   *     // show loading indicator ...
   *   };
   *
   *   routeManager.addEventListener('routeChangeStart', handleRouteStart);
   * ```
   *
   * @param event The router event name (routeChangeStart, routeChangeComplete).
   * @param callback The function to execute when the event occurs.
   */
  public addEventListener = (
    event: RouterEvent,
    callback: (...args: any[]) => void
  ): void => {
    this.log(`Adding Router Event Listener: ${event}`, 'info');
    this.eventListener(event, callback, true);
  };

  /**
   * Un-registers a previously added router event listener to prevent memory
   * leaks and unwanted event handling. This should be called during component
   * cleanup or when event handling is no longer required.
   *
   * ```typescript
   *   import routeManager from '@ion/applications/src/routes/RouteManager';
   *   ...
   *
   *   const handleRouteStart = (url) => {
   *     // handle route change ...
   *   };
   *
   *   // cleanup event listener ...
   *   routeManager.removeEventListener('routeChangeStart', handleRouteStart);
   * ```
   *
   * @param event The router event name to remove the listener from.
   * @param callback The specific callback function to unregister.
   */
  public removeEventListener = (
    event: RouterEvent,
    callback: (...args: any[]) => void
  ): void => {
    this.log(`Removing Router Event Listener: ${event}`, 'info');
    this.eventListener(event, callback, false);
  };

  /**
   * Performs programmatic navigation to the specified route with optional
   * query parameters and history management. This method provides consistent
   * navigation behavior across the iOn Platform applications and supports
   * complex routing scenarios.
   *
   * ```typescript
   *   import routeManager from '@ion/applications/src/routes/RouteManager';
   *   ...
   *
   *   // navigate with new history entry ...
   *   routeManager.navigate({
   *     pathname: '/dashboard/analytics',
   *     query: {tab: 'reports', filter: 'monthly'}
   *   });
   *
   *   // navigate by replacing the current history entry ...
   *   routeManager.navigate({
   *     pathname: '/settings',
   *     replace: true
   *   });
   * ```
   *
   * @param params An object containing the pathname and optional query parameters.
   * @param params.pathname The target route path for navigation.
   * @param params.query Optional query parameters to append to the route.
   * @param params.replace Optional flag to replace current history entry instead
   *        of adding new one.
   */
  public navigate = ({pathname, query, replace = false}: NavigationParams): void => {
    if (this._router) {
      const {
        isValid, params, method, target
      }: NavigationPreparationParams = this.preparedNavigationParams(pathname, query, replace);
      const message = `Navigating to: ${target} via router.${method}`;
      if (!isValid) {
        this.log(`${message}. New route: ${pathname} is the current route: ${this.getAsPath()}`, 'warn');
      } else {
        this.log(message, 'info');
      }
      this._router[method]({pathname, query: params});
    } else {
      this.log(`RouteManager not initialized. Failed to Navigate to: ${pathname}`, 'error');
    }
  };

  /**
   * Prepares and validates navigation parameters for Next.js router operations.
   * This internal utility constructs the target URL, validates navigation
   * necessity, processes query parameters, and determines the appropriate
   * router method for the navigation request.
   *
   * @param   pathname The target route path for navigation.
   * @param   query Optional query string to append to the pathname.
   * @param   replace Optional flag indicating whether to replace current history entry.
   * @returns Object containing prepared navigation parameters:
   *          `isValid`: `true` if navigation should proceed, `false` if target equals
   *           current route.
   *          `params`: Processed query parameters (portion after '?' if present)
   *          `method`: Router method to use ('push' or 'replace')
   *          `target`: Complete target URL with pathname and query string
   */
  private preparedNavigationParams(
    pathname: string, query?: string, replace?: boolean): NavigationPreparationParams {
    const isProcessedQuery = query?.includes('?');
    const target           = query ? (
      `${pathname}${isProcessedQuery ? query : `?${query}`}`
    ) : pathname;
    return {
      isValid : target !== this.getAsPath(),
      method  : replace ? 'replace' : 'push',
      params  : isProcessedQuery ? query?.split('?')[1] : query,
      target
    };
  }

  /**
   * Internal method which allows for registering and unregistering router event listener
   * callbacks..
   *
   * @param event    The name of the router event for which the listener is to be added.
   * @param callback The function to be executed when the given event occurs.
   * @param isAdd    When `true` (default), adds the event listener, otherwise, when
   *                 `false`, the given listener will be removed.
   */
  private eventListener = (
    event: string,
    callback: (...args: any[]) => void,
    isAdd = true
  ): void => {
    if (this._router) {
      this._router.events[isAdd ? 'on' : 'off'](event as any, callback);
    }
  };

  /**
   * Provides an internal logging facility to ensure consistency in logs and allow for
   * easy categorization of RouteManager Logging.
   *
   * @param message
   * @param type
   */
  private log(message, type: 'info' | 'warn' | 'error') {
    logger?.[type](`(RouteManager API) - ${message}`);
  };

  /**
   * @private
   *
   * IMPORTANT: This method is primarily intended for verification purposes
   * to reset instance state and should not be used directly in client code.
   */
  public __reset__ = (): void => {
    this._router = null;
  };

  /**
   * @private
   *
   * IMPORTANT: This method is primarily intended for verification purposes
   * to verify internal state and should not be used directly in client code.
   */
  public __getRouter__ = (): NextRouter | null => this._router;
}

export default new RouterManager();
